import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { userDetail } from "@/apis/user";
import { setIsOpen, setUserInfo } from "@/store/modules/user";
import {
  EMBED_AUTH_ERROR,
  EMBED_AUTH_INVALID,
  EMBED_AUTH_LOGOUT,
  EMBED_AUTH_REFRESHED,
  EMBED_AUTH_REQUEST,
  EMBED_AUTH_RESPONSE,
  EMBED_AUTH_SESSION_VALID,
  HOST_AUTH_LOGOUT_EVENT,
} from "./authBridge";
import styles from "./index.module.scss";

//  OpenGA 嵌入式 URL
const OPENGA_URL =
  process.env.REACT_APP_OPENGA_IFRAME_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:3030/home"
    : `${window.location.origin}/openGAiframe/home`);

// 页面加载错误提示
const PAGE_LOAD_ERROR = "页面暂时无法加载，请稍后重试";

//  OpenGA 组件
const OpenGA = () => {
  const dispatch = useDispatch();
  const iframeRef = useRef(null);
  const credentialRefreshAttemptedRef = useRef(false);
  const userInfo = useSelector((state) => state.user.userInfo);
  const { token: ogaToken, refreshToken: ogaRefreshToken } = userInfo;
  const [error, setError] = useState("");

  const iframeSrc = useMemo(() => new URL(OPENGA_URL, window.location.href).toString(), []);
  const iframeOrigin = useMemo(() => new URL(iframeSrc).origin, [iframeSrc]);

  // 向 iframe 发送消息
  const postToIframe = useCallback(
    (data) => {
      if (!iframeRef.current?.contentWindow) return;
      iframeRef.current.contentWindow.postMessage(data, iframeOrigin);
    },
    [iframeOrigin],
  );

  // OpenGA 使用 OGA 的登录会话；OGE 账户绑定只影响计算能力，不影响页面认证。
  const sendHostAuthToIframe = useCallback(() => {
    const currentToken = localStorage.getItem("token") || ogaToken;
    const currentRefreshToken = localStorage.getItem("refreshToken") || ogaRefreshToken;
    if (!currentToken) return;
    postToIframe({
      type: EMBED_AUTH_RESPONSE,
      credentials: {
        ogaToken: currentToken,
        ogaRefreshToken: currentRefreshToken,
        uuid: userInfo.uuid,
        username: userInfo.username,
        nickname: userInfo.nickname,
      },
    });
  }, [ogaRefreshToken, ogaToken, postToIframe, userInfo.nickname, userInfo.username, userInfo.uuid]);

  const retryOpenGaAuth = useCallback(() => {
    credentialRefreshAttemptedRef.current = false;
    setError("");
    sendHostAuthToIframe();
  }, [sendHostAuthToIframe]);

  // OGA 登录状态变化后重置认证恢复状态。
  useEffect(() => {
    credentialRefreshAttemptedRef.current = false;
    setError("");
    if (!ogaToken) dispatch(setIsOpen(true));
  }, [dispatch, ogaToken]);

  // 在 iframe 加载后传递 OGA 会话，不依赖是否绑定 OGE 账户。
  useEffect(() => {
    sendHostAuthToIframe();
  }, [sendHostAuthToIframe]);

  // OGA 主站退出时同步清理已挂载 iframe 的认证状态。
  useEffect(() => {
    const handleHostLogout = () => postToIframe({ type: EMBED_AUTH_LOGOUT });
    window.addEventListener(HOST_AUTH_LOGOUT_EVENT, handleHostLogout);
    return () => window.removeEventListener(HOST_AUTH_LOGOUT_EVENT, handleHostLogout);
  }, [postToIframe]);

  //  监听 OpenGA 嵌入式消息
  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (event.source !== iframeRef.current?.contentWindow || event.origin !== iframeOrigin)
        return;
      if (event.data?.type === EMBED_AUTH_REQUEST) {
        const cachedOgaToken = event.data?.ogaToken;
        const currentToken = localStorage.getItem("token") || ogaToken;
        if (cachedOgaToken && cachedOgaToken === currentToken) {
          postToIframe({ type: EMBED_AUTH_SESSION_VALID });
          return;
        }
        sendHostAuthToIframe();
      } else if (event.data?.type === EMBED_AUTH_REFRESHED) {
        const { token, refreshToken } = event.data?.auth || {};
        if (token && refreshToken) {
          dispatch(setUserInfo({ token, refreshToken }));
        }
      } else if (event.data?.type === EMBED_AUTH_ERROR) {
        setError(PAGE_LOAD_ERROR);
      } else if (event.data?.type === EMBED_AUTH_INVALID) {
        if (credentialRefreshAttemptedRef.current) {
          setError(PAGE_LOAD_ERROR);
          dispatch(setIsOpen(true));
          return;
        }
        credentialRefreshAttemptedRef.current = true;
        userDetail()
          .then(sendHostAuthToIframe)
          .catch(() => dispatch(setIsOpen(true)));
      }
    };

    window.addEventListener("message", handleIframeMessage);
    return () => window.removeEventListener("message", handleIframeMessage);
  }, [
    dispatch,
    iframeOrigin,
    ogaToken,
    postToIframe,
    sendHostAuthToIframe,
  ]);

  if (!ogaToken) {
    return <div className={styles.status} />;
  }
  //  显示登录状态
  if (error) {
    return (
      <div className={styles.status}>
        <Alert
          showIcon
          type="error"
          message="页面加载失败"
          description={error}
          action={<Button onClick={retryOpenGaAuth}>重试</Button>}
        />
      </div>
    );
  }
  //  显示 OpenGA 嵌入式 iframe
  return (
    <div className={styles.container}>
      <iframe
        ref={iframeRef}
        title="时空智能体共享服务平台"
        src={iframeSrc}
        className={styles.iframe}
        onLoad={sendHostAuthToIframe}
      />
    </div>
  );
};

export default OpenGA;
